import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { toLocalInput } from "../message-content";

const styles = {
  content: { pt: 1 } satisfies SxProps<Theme>,
};

type ScheduleDialogProps = {
  readonly open: boolean;
  readonly title: string;
  readonly initial: Date | null;
  readonly onClose: () => void;
  /** Resolves when saved; rejects with the reason to show. */
  readonly onSubmit: (startsAt: Date) => Promise<void>;
};

/** Picks a start time in the operator's time zone; the server stores UTC. */
const ScheduleDialog = ({
  open,
  title,
  initial,
  onClose,
  onSubmit,
}: ScheduleDialogProps) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const wasOpen = useRef(false);

  // Seed only as the dialog opens: a refused submit must keep the chosen time.
  useEffect(() => {
    if (open && !wasOpen.current) {
      setValue(initial === null ? "" : toLocalInput(initial));
      setError(null);
    }
    wasOpen.current = open;
  }, [open, initial]);

  const handleSubmit = async () => {
    if (value === "") {
      setError("Choose when delivery starts.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(new Date(value));
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : String(submitError),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.content}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            type="datetime-local"
            label="Starts at"
            value={value}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: toLocalInput(new Date()) }}
            helperText={`Your time zone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => {
            void handleSubmit();
          }}
        >
          Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;
